import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useStore from '@/store/useStore';
import PublicLiveScoreboard from '@/components/match/PublicLiveScoreboard';
import { Match, Team, Player } from '@/types/cricket';
import useSupabaseRealtime from '@/hooks/useSupabaseRealtime';

// Mock data for demonstration
const mockPlayersTeamA: Player[] = [
  { id: 'p1', name: 'Rohit Sharma', role: 'batsman', teamIds: ['t1'] },
  { id: 'p2', name: 'Shubman Gill', role: 'batsman', teamIds: ['t1'] },
  { id: 'p3', name: 'Virat Kohli', role: 'batsman', teamIds: ['t1'] },
  { id: 'p4', name: 'Hardik Pandya', role: 'all-rounder', teamIds: ['t1'] },
  { id: 'p5', name: 'Jasprit Bumrah', role: 'bowler', teamIds: ['t1'] },
];
const mockPlayersTeamB: Player[] = [
  { id: 'p6', name: 'David Warner', role: 'batsman', teamIds: ['t2'] },
  { id: 'p7', name: 'Travis Head', role: 'batsman', teamIds: ['t2'] },
  { id: 'p8', name: 'Steve Smith', role: 'batsman', teamIds: ['t2'] },
  { id: 'p9', name: 'Pat Cummins', role: 'bowler', teamIds: ['t2'] },
  { id: 'p10', name: 'Mitchell Starc', role: 'bowler', teamIds: ['t2'] },
];

const mockTeams: Team[] = [
  {
    id: 't1',
    name: 'India',
    shortCode: 'IND',
    color: '#1c4e80',
    players: mockPlayersTeamA,
    logoUrl: '',
  },
  {
    id: 't2',
    name: 'Australia',
    shortCode: 'AUS',
    color: '#f9c846',
    players: mockPlayersTeamB,
    logoUrl: '',
  },
];

const mockMatch: Match = {
  id: 'm1',
  tournamentId: 'tour1',
  team1Id: 't1',
  team2Id: 't2',
  matchDate: new Date().toISOString(),
  city: 'Mumbai',
  status: 'live',
  team1Score: { runs: 178, wickets: 4, overs: 18, balls: 2 },
  team2Score: { runs: 0, wickets: 0, overs: 0, balls: 0 },
  currentInnings: 1,
  currentBattingTeam: 't1',
  matchType: 'T20',
  totalOvers: 20,
  ballsPerOver: 6,
  totalWickets: 10,
  wicketsPerInnings: 10,
  totalInnings: 2,
  tossWinnerId: 't2',
  tossSelectedBattingTeam: 't1',
  team1CaptainId: 'p1',
  team2CaptainId: 'p8',
};

const LiveScoreDashboard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { matches, teams, fetchMatches, fetchTeams, loading } = useStore();
  useSupabaseRealtime();

  // Initial fetch
  React.useEffect(() => {
    if (matches.length === 0) fetchMatches();
    if (teams.length === 0) fetchTeams();
  }, [fetchMatches, fetchTeams, matches.length, teams.length]);

  const match = matches.find(m => m.id === id);
  const matchTeams = match
    ? teams.filter(t => t.id === match.team1Id || t.id === match.team2Id)
    : mockTeams;

  if (loading.matches || loading.teams) {
    return <div className="text-center py-8">Loading...</div>;
  }
  if (!match) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-bold mb-4">Match not found</h2>
        <button className="btn btn-primary" onClick={() => navigate('/matches')}>Return to Matches</button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-6">
      <h1 className="text-3xl font-bold text-center mb-2">Live Match Score</h1>
      <PublicLiveScoreboard match={match} teams={matchTeams} />
    </div>
  );
};

export default LiveScoreDashboard; 